import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import type { DocsWithCnpjKpisResponse, NovasBaixadasResponse, StatusDetail,
  UnitsByUFResponse, UnitsKpisResponse } from '../hooks/useUnitsData';
import './unidades-print.css';

export interface UnitsPrintSnapshot {
  kpis: UnitsKpisResponse;
  unitsByUF: UnitsByUFResponse[];
  novasBaixadas: NovasBaixadasResponse[];
  docs: DocsWithCnpjKpisResponse;
  regional: string | null;
  uf: string | null;
  selectedYear: number;
  generatedAt: string;
  lastSync: string | null;
}

const number = (value: number) => value.toLocaleString('pt-BR');
const date = (value: string) => new Date(value).toLocaleString('pt-BR');
const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const statusColors = ['#047857', '#b45309', '#dc2626'];

function Detail({ detail }: { detail: StatusDetail }) {
  return <div className="up-detail">
    <span>Vigente: <b>{number(detail.vigente)}</b></span>
    <span>Permanente: <b>{number(detail.permanente)}</b></span>
    <span>Isento: <b>{number(detail.isento)}</b></span>
    <span>Vigente em renovação: <b>{number(detail.vigenteEmRenovacao)}</b></span>
  </div>;
}

function Distribution({ title, data }: { title: string; data: { regular: number; em_andamento: number; irregular: number } }) {
  const values = [data.regular, data.em_andamento, data.irregular];
  const total = values.reduce((a, b) => a + b, 0);
  const labels = ['Regular', 'Em andamento', 'Irregular'];
  let offset = 0;
  return <section className="up-card up-distribution">
    <h2>{title}</h2>
    <div className="up-distribution-content">
      <svg viewBox="0 0 120 120" role="img" aria-label={`${title}: ${number(total)} no total`}>
        <circle cx="60" cy="60" r="44" fill="none" stroke="#e2e8f0" strokeWidth="15" />
        {values.map((value, index) => {
          const length = total ? value / total * 100 : 0;
          const start = offset;
          offset += length;
          return <circle key={labels[index]} cx="60" cy="60" r="44" fill="none" stroke={statusColors[index]}
            strokeWidth="15" pathLength="100" strokeDasharray={`${length} ${100 - length}`}
            strokeDashoffset={-start} transform="rotate(-90 60 60)" />;
        })}
        <text x="60" y="59" textAnchor="middle" fontSize="20" fontWeight="700" fill="#0f172a">{number(total)}</text>
        <text x="60" y="75" textAnchor="middle" fontSize="11" fill="#475569">Total</text>
      </svg>
      <table aria-label={title}><tbody>{values.map((value, index) => <tr key={labels[index]}>
        <th><i style={{ background: statusColors[index] }} />{labels[index]}</th>
        <td><b>{number(value)}</b></td>
        <td>{total ? (value / total * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : '0'}%</td>
      </tr>)}</tbody></table>
    </div>
  </section>;
}

function PrintMap({ data, type, ready, onReady }: {
  data: UnitsByUFResponse[]; type: 'alvara' | 'avcb'; ready: boolean; onReady: () => void;
}) {
  const values = data.map(row => ({ name: row.uf, value: (type === 'alvara' ? row.alvaraRegular : row.avcbRegular) ?? 0 }));
  const option: EChartsOption = {
    animation: false,
    textStyle: { fontFamily: 'Arial', color: '#334155' },
    visualMap: { min: 0, max: Math.max(1, ...values.map(row => row.value)), orient: 'horizontal',
      left: 'center', bottom: 0, itemHeight: 100, itemWidth: 10, calculable: false,
      text: ['Mais regulares', 'Menos regulares'], textStyle: { fontSize: 11, color: '#334155' },
      inRange: { color: ['#ecfdf5', '#6ee7b7', '#047857'] } },
    series: [{ type: 'map', map: 'brazil-print', roam: false, silent: true,
      layoutCenter: ['50%', '43%'], layoutSize: '83%',
      itemStyle: { areaColor: '#f1f5f9', borderColor: '#64748b', borderWidth: 0.5 },
      data: values }],
  };
  return <section className="up-card up-map">
    <h2>Regularidade por UF - {type === 'alvara' ? 'Alvará' : 'AVCB'}</h2>
    <p>{number(values.reduce((sum, row) => sum + row.value, 0))} unidades regulares no recorte</p>
    {ready && <ReactECharts option={option} opts={{ renderer: 'svg' }} style={{ width: '100%', height: '61mm' }} onChartReady={onReady} />}
  </section>;
}

function Evolution({ data, year }: { data: NovasBaixadasResponse[]; year: number }) {
  const max = Math.max(2, Math.ceil(Math.max(0, ...data.flatMap(row => [row.novas, row.baixadas])) / 2) * 2);
  const x = (index: number) => 52 + index * (876 / Math.max(1, data.length - 1));
  const y = (value: number) => 143 - (value / max * 110);
  return <section className="up-card up-evolution">
    <h2>Novas x baixadas - {year}</h2>
    {data.length ? <>
      <svg viewBox="0 0 980 186" role="img" aria-label={`Evolução de novas e baixadas em ${year}`}>
        {[0, 0.5, 1].map(fraction => <g key={fraction}>
          <line x1="52" x2="928" y1={y(max * fraction)} y2={y(max * fraction)} stroke="#cbd5e1" strokeDasharray="3 3" />
          <text x="42" y={y(max * fraction) + 4} textAnchor="end" fontSize="11" fill="#475569">{number(Math.ceil(max * fraction))}</text>
        </g>)}
        {(['novas', 'baixadas'] as const).map((key, index) => <g key={key}>
          <polyline points={data.map((row, i) => `${x(i)},${y(row[key])}`).join(' ')} fill="none" stroke={statusColors[index * 2]} strokeWidth="2.5" />
          {data.map((row, i) => <circle key={row.month} cx={x(i)} cy={y(row[key])} r="3" fill={statusColors[index * 2]} />)}
        </g>)}
        {data.map((row, i) => <text key={row.month} x={x(i)} y="163" textAnchor="middle" fontSize="12" fill="#334155">{months[Number(row.month.slice(5, 7)) - 1]}</text>)}
        <text x="410" y="183" textAnchor="middle" fontSize="12" fill={statusColors[0]}>● Novas</text>
        <text x="550" y="183" textAnchor="middle" fontSize="12" fill={statusColors[2]}>● Baixadas</text>
      </svg>
      <table className="up-months" aria-label={`Movimentação mensal de ${year}`}><thead><tr><th>Mês</th>{data.map(row => <th key={row.month}>{months[Number(row.month.slice(5, 7)) - 1]}</th>)}</tr></thead>
        <tbody>{(['novas', 'baixadas', 'total'] as const).map(key => <tr key={key}><th>{key === 'total' ? 'Total de unidades' : key === 'novas' ? 'Novas' : 'Baixadas'}</th>{data.map(row => <td key={row.month}>{number(row[key] ?? 0)}</td>)}</tr>)}</tbody>
      </table>
    </> : <p className="up-empty">Sem dados disponíveis para o ano selecionado.</p>}
  </section>;
}

export function UnidadesPrintReport({ snapshot: s, onClose }: { snapshot: UnitsPrintSnapshot; onClose: () => void }) {
  const [mapReady, setMapReady] = useState(false);
  const [mapsRendered, setMapsRendered] = useState({ alvara: false, avcb: false });
  const [fontsReady, setFontsReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const closeCallback = useRef(onClose);
  closeCallback.current = onClose;

  useEffect(() => {
    const controller = new AbortController();
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeCallback.current();
      if (event.key === 'Tab') {
        const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('.up-toolbar button:not(:disabled)'));
        const target = event.shiftKey ? buttons[buttons.length - 1] : buttons[0];
        if ((event.shiftKey && document.activeElement === buttons[0]) || (!event.shiftKey && document.activeElement === buttons[buttons.length - 1])) {
          event.preventDefault(); target?.focus();
        }
      }
    };
    document.addEventListener('keydown', keydown);
    document.fonts.ready.then(() => { if (!controller.signal.aborted) setFontsReady(true); });
    const timeout = window.setTimeout(() => { setMapError(true); controller.abort(); }, 20000);
    fetch('/data/maps/brazil.json', { signal: controller.signal }).then(async response => {
      if (!response.ok) throw new Error('Mapa indisponível');
      const geoJson = await response.json();
      for (const feature of geoJson.features) {
        feature.properties.name = feature.properties.SIGLA || feature.properties.Sigla || feature.properties.name;
      }
      if (!controller.signal.aborted) {
        echarts.registerMap('brazil-print', geoJson);
        setMapReady(true);
      }
    }).catch(() => { if (!controller.signal.aborted) setMapError(true); })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      window.clearTimeout(timeout); controller.abort();
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', keydown);
      previouslyFocused?.focus();
    };
  }, []);

  const printable = fontsReady && mapsRendered.alvara && mapsRendered.avcb;
  const history = s.novasBaixadas.filter(row => row.month.startsWith(`${s.selectedYear}-`)).sort((a, b) => a.month.localeCompare(b.month));
  const novas = history.reduce((sum, row) => sum + row.novas, 0);
  const baixadas = history.reduce((sum, row) => sum + row.baixadas, 0);
  const detail = s.docs.regularDetail;
  const page = (pageNumber: number, subtitle: string, content: ReactNode) => <article className="up-page">
    <header className="up-header print-header"><div><p>ATIVA GESTÃO · CRAS DATA</p><h1>Unidades <span>/ {subtitle}</span></h1></div>
      <div className="up-context"><b>Regional: {s.regional || 'Todas'} · UF: {s.uf || 'Todas'}</b><span>Emitido em {date(s.generatedAt)}</span></div>
    </header>
    {content}
    <footer className="up-footer"><span>Fonte: PropertyDocs · {s.lastSync ? `Última sincronização: ${date(s.lastSync)}` : 'Dados carregados no painel'}</span><span>{pageNumber} / 2</span></footer>
  </article>;

  return createPortal(<div className="units-print-root" role="dialog" aria-modal="true" aria-label="Prévia de impressão A4" data-print-ready={printable}>
    <div className="up-toolbar"><div><strong>Prévia A4 · 2 páginas · Paisagem</strong><p>Margens de 10 mm. Se o navegador adicionar cabeçalhos e rodapés, desative-os.</p></div>
      <div><button ref={closeRef} onClick={onClose}>Voltar</button><button disabled={!printable} onClick={() => window.print()}>{printable ? 'Imprimir / Salvar PDF' : 'Preparando gráficos...'}</button></div>
      {mapError && <p role="alert">Não foi possível carregar os mapas. Volte e tente novamente; a impressão foi bloqueada para evitar um relatório incompleto.</p>}
    </div>
    {page(1, 'Visão geral', <>
      <div className="up-kpis">
        <section className="up-card"><h2>Total de unidades</h2><strong className="up-number">{number(s.kpis.totalUnidades)}</strong><p>Unidades no recorte selecionado</p></section>
        {(['alvara', 'avcb'] as const).map(type => <section className="up-card" key={type}><h2>{type === 'alvara' ? 'Alvará de funcionamento' : 'AVCB'}</h2><strong className="up-number">{number(s.kpis[type].regular)} <small>regulares</small></strong><Detail detail={s.kpis[type].detail} /></section>)}
      </div>
      <div className="up-maps">
        <PrintMap data={s.unitsByUF} type="alvara" ready={mapReady} onReady={() => setMapsRendered(prev => prev.alvara ? prev : { ...prev, alvara: true })} />
        <PrintMap data={s.unitsByUF} type="avcb" ready={mapReady} onReady={() => setMapsRendered(prev => prev.avcb ? prev : { ...prev, avcb: true })} />
      </div>
      <div className="up-farols"><Distribution title="Farol Alvará" data={s.kpis.alvara} /><Distribution title="Farol AVCB" data={s.kpis.avcb} /></div>
    </>)}
    {page(2, 'Documentos e movimentação', <>
      <div className="up-docs">
        <section className="up-card"><h2>Documentos com CNPJ (ativos)</h2><strong className="up-number">{number(s.docs.regular)} <small>regulares</small></strong>
          <p>{number(s.docs.totalDocumentos)} documentos no total</p>
          <Detail detail={{ vigente: detail.vigente, permanente: detail.permanente, isento: detail.isento, vigenteEmRenovacao: detail.vigenteRenovacao }} />
          <p>Contagem de documentos, não de unidades.</p>
        </section><Distribution title="Documentos com CNPJ - distribuição por status" data={s.docs} />
      </div>
      <div className="up-movement"><h2>Movimentação de unidades · {s.selectedYear}</h2><span><b>{number(novas)}</b> novas</span><span><b>{number(baixadas)}</b> baixadas</span><span>Saldo: <b>{number(novas - baixadas)}</b></span></div>
      <Evolution data={history} year={s.selectedYear} />
    </>)}
  </div>, document.body);
}
